import type { FC, MouseEvent, ReactNode, ComponentType } from "react";

import classNames from "classnames";
import styled from "styled-components";

import type { ITooltipProps } from "@similarweb/ui-components";
import { Tooltip } from "@similarweb/ui-components";

import { StyledCellButtonText, StyledIconButton } from "app/components/React/Table/cells/ExpansionButtonCell";
import type { ITableCellProps } from "app/components/React/Table/interfaces/ITableCellProps";
import { CenteredFlexRow, FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

const CellWrapper = styled(FlexRow)<{ isButtonLeftSide: boolean; contentAlignment: string }>`
    justify-content: ${({ contentAlignment }) => contentAlignment};
    width: 100%;
    height: 100%;
    ${({ isButtonLeftSide }) => isButtonLeftSide && "flex-direction: row-reverse"};
    gap: 8px;
    align-items: center;
`;

export interface IEnrichButtonCellProps extends Pick<ITableCellProps, "row"> {
    buttonElement?: ReactNode;
    isDisabled?: boolean;
    children?: ReactNode;
    onCellClick?: (clickEvent: MouseEvent, index: number) => void;
    onEnrichButtonClick?: (clickEvent: MouseEvent) => void;
    enabledTooltip?: boolean;
    markOnHoverOnRow?: boolean;
    tooltipText?: string;
    isButtonLeftSide?: boolean;
    presentEnrichButton?: boolean;
    contentAlignment?: string;
    tooltipComponent?: ComponentType<ITooltipProps>;
}

export const EnrichButtonCell: FC<IEnrichButtonCellProps> = (props) => {
    const {
        onCellClick,
        onEnrichButtonClick,
        isDisabled,
        children,
        row,
        enabledTooltip,
        tooltipText,
        markOnHoverOnRow,
        isButtonLeftSide,
        buttonElement,
        presentEnrichButton,
        contentAlignment = "space-between",
        tooltipComponent: TooltipComponent = Tooltip,
    } = props;
    const onClickCallback = (clickEvent: MouseEvent) => {
        clickEvent.stopPropagation();
        onCellClick?.(clickEvent, row?.index);
        onEnrichButtonClick?.(clickEvent);
    };
    return (
        <CellWrapper
            data-enrich-button
            isButtonLeftSide={!!isButtonLeftSide}
            data-automation={"expand-row-cell-container"}
            contentAlignment={contentAlignment}
        >
            {children}
            {presentEnrichButton ? (
                <TooltipComponent
                    key={`EnrichButtonCellTooltip${row?.value}`}
                    enabled={enabledTooltip}
                    content={tooltipText}
                >
                    <CenteredFlexRow>
                        {!row?.parent ? (
                            <StyledIconButton
                                height={24}
                                onClick={isDisabled ? undefined : onClickCallback}
                                className={classNames(
                                    "enrich",
                                    markOnHoverOnRow && "markOnHoverOnRow",
                                    isDisabled && "enrich--disabled",
                                )}
                                iconName={"chev-down"}
                                isDisabled={isDisabled}
                                width={buttonElement ? "auto" : "24px"}
                                iconSize={"xs"}
                                padding={"4px"}
                            >
                                {buttonElement ? (
                                    <StyledCellButtonText isDisabled={isDisabled}>{buttonElement}</StyledCellButtonText>
                                ) : null}
                            </StyledIconButton>
                        ) : null}
                    </CenteredFlexRow>
                </TooltipComponent>
            ) : null}
        </CellWrapper>
    );
};
